import { createContext, useContext, ReactNode, useMemo } from 'react';
import { LocationAirSettingsDefaultSources } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/service.pb';
import { useGetAirSettingsDefaultSourcesQuery } from '@frontend/api-ai-settings';

export interface AIRSettingsDefaultSourcesContextValue {
  locationDefaultSourcesMap: Record<string, LocationAirSettingsDefaultSources>;
  isLoadingDefaultSources: boolean;
}

const AIRSettingsDefaultSourcesContext = createContext<AIRSettingsDefaultSourcesContextValue | undefined>(undefined);

interface AIRSettingsDefaultSourcesProviderProps {
  locationIds: string[];
  children: ReactNode;
}

export const AIRSettingsDefaultSourcesProvider = ({
  locationIds,
  children,
}: AIRSettingsDefaultSourcesProviderProps) => {
  const { data: locationDefaultSourcesResponse, isLoading: isLoadingDefaultSources } =
    useGetAirSettingsDefaultSourcesQuery({
      request: {
        locationIds,
      },
      options: {
        enabled: locationIds.length > 0,
      },
    });

  const contextValue = useMemo(() => {
    const locationDefaultSourcesMap =
      locationDefaultSourcesResponse?.locationAirSettingsDefaultSources?.reduce<
        Record<string, LocationAirSettingsDefaultSources>
      >((acc, curr) => {
        if (curr.locationId) {
          acc[curr.locationId] = curr;
        }
        return acc;
      }, {}) ?? {};
    return {
      locationDefaultSourcesMap,
      isLoadingDefaultSources,
    };
  }, [locationDefaultSourcesResponse, isLoadingDefaultSources]);

  return (
    <AIRSettingsDefaultSourcesContext.Provider value={contextValue}>
      {children}
    </AIRSettingsDefaultSourcesContext.Provider>
  );
};

export const useAIRSettingsDefaultSources = () => {
  const context = useContext(AIRSettingsDefaultSourcesContext);
  if (context === undefined) {
    throw new Error('useAIRSettingsDefaultSources must be used within an AIRSettingsDefaultSourcesProvider');
  }
  return context;
};
